import { useEffect } from 'react';
import { CheckCircle2, Undo2, RotateCcw } from 'lucide-react';
import { useTasksByStatus, useSubtasks, updateTask } from '../../hooks/useTasks';
import { useAppStore } from '../../store';
import { useContextMenu } from '../../hooks/useContextMenu';
import { queryClient } from '../../api/queryClient';
import type { Task } from '../../types';
import { format } from 'date-fns';
import { getTodayIso } from '../../lib/dateScope';

export function ArchiveView() {
  const tasks = useTasksByStatus('done');
  const setHeaderContext = useAppStore((s) => s.setHeaderContext);
  const parentTasks = tasks
    .filter((t) => !t.parentId)
    .sort((a, b) => new Date(b.completedAt ?? 0).getTime() - new Date(a.completedAt ?? 0).getTime());

  useEffect(() => {
    setHeaderContext({ count: parentTasks.length, label: 'completed' });
    return () => setHeaderContext(null);
  }, [parentTasks.length, setHeaderContext]);

  if (parentTasks.length === 0) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-48px)]">
        <div className="text-center text-text-muted">
          <p className="text-headline mb-2">No completed items yet</p>
          <p className="text-body">Complete projects or tasks on the track to see them here.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <div className="mb-6">
        <h2 className="text-headline text-text-primary">Completed</h2>
        <p className="text-body text-text-muted mt-1">{parentTasks.length} items achieved</p>
      </div>
      <div className="space-y-3">
        {parentTasks.map((task) => (
          <ArchivedItem key={task.id} task={task} />
        ))}
      </div>
    </div>
  );
}

function ArchivedItem({ task }: { task: Task }) {
  const subtasks = useSubtasks(task.id);
  const ctxMenu = useContextMenu();
  const spaceId = useAppStore((s) => s.currentSpaceId);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['tasks', spaceId, 'done'] });

  const resurrectToBacklog = async () => {
    await updateTask(task.id, { status: 'backlog', completedAt: undefined, trackProgress: 0 });
    invalidate();
  };

  const resurrectToTrack = async () => {
    await updateTask(task.id, { status: 'track', trackDate: getTodayIso(), completedAt: undefined, trackProgress: 0 });
    invalidate();
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    ctxMenu.open(e.clientX, e.clientY, [
      { label: 'Move to Backlog', icon: <Undo2 size={13} />, onClick: resurrectToBacklog },
      { label: 'Move to Track (today)', icon: <RotateCcw size={13} />, onClick: resurrectToTrack },
    ]);
  };

  return (
    <div className="surface-card overflow-hidden hover:border-border-default transition-colors group" onContextMenu={handleContextMenu}>
      <div className="flex items-center gap-4 p-4">
        <div className="w-10 h-10 rounded-xl bg-finish/10 flex items-center justify-center shrink-0">
          <CheckCircle2 size={20} className="text-finish" />
        </div>
        <div className="min-w-0 flex-1">
          <button
            onClick={() => useAppStore.getState().setSelectedTaskId(task.id)}
            className="text-body font-semibold text-text-primary hover:text-accent transition-colors text-left truncate block w-full"
          >
            {task.title}
          </button>
          <div className="flex items-center gap-3 mt-1">
            <span className="text-micro text-text-muted capitalize">{task.itemType}</span>
            {task.completedAt && (
              <span className="text-micro text-finish font-medium">
                {format(new Date(task.completedAt), 'MMM d, yyyy')}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
          <button onClick={resurrectToBacklog} className="p-2 rounded-lg interactive-ghost text-text-muted hover:text-accent" title="Move to backlog">
            <Undo2 size={14} />
          </button>
          <button onClick={resurrectToTrack} className="p-2 rounded-lg interactive-ghost text-text-muted hover:text-finish" title="Move to track">
            <RotateCcw size={14} />
          </button>
        </div>
      </div>

      {subtasks.length > 0 && (
        <div className="border-t border-border-subtle px-5 py-3 pl-[76px] space-y-1.5">
          {subtasks.map((st) => (
            <div key={st.id} className="flex items-center gap-2.5 text-caption">
              {st.status === 'done' ? (
                <CheckCircle2 size={12} className="text-finish/50 shrink-0" />
              ) : (
                <div className="w-3 h-3 rounded-full border border-text-muted shrink-0" />
              )}
              <span className={st.status === 'done' ? 'text-text-muted' : 'text-text-secondary'}>
                {st.title}
              </span>
              <span className="text-micro text-text-muted ml-auto">
                {st.status === 'done' ? 'done' : `${st.trackProgress}%`}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
